import { DatePicker, Form, message, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch } from "../../../../../../redux/store";
import { getBookingDetail } from "../../../ducks/actions";
import { useGetMasterUsers } from "../../../../../../api/v1/useGetMasterUsers";
import { useEarlyTerminateLease } from "../api/v1/useEarlyTerminateLease";
import { ActionModal } from "../ActionModal";
import { Booking } from "../../../../../../types/bookings";

type Step = "confirmation" | "finalization";

type FormValues = {
  vacatingDate: Dayjs | null;
  notifyUsers: string[];
};

type LeaseActionModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const TerminateLease = (props: LeaseActionModalProps) => {
  const { isOpen, onClose } = props;
  const { leaseId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [step, setStep] = useState<Step>("confirmation");

  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const { leaseStatus, leaseTerms } = (bookingDetails || {}) as Booking;

  const onSuccessHandler = () => {
    dispatch(getBookingDetail(leaseId));
    message.success("Approval request forwarded successfully.");
  };

  const { masterUsers, isLoadingMasterUsers } = useGetMasterUsers({ isEnabled: true });
  const { earlyTerminateLease, isPendingEarlyTerminateLease } = useEarlyTerminateLease({
    onSuccess: () => onSuccessHandler(),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      vacatingDate: null,
      notifyUsers: [],
    },
  });

  const isExpiredWithPastEndDate =
    leaseStatus === "lease_expired" &&
    leaseTerms?.leaseEndDate &&
    dayjs(leaseTerms.leaseEndDate).isBefore(dayjs(), "day");

  const minVacatingDate = isExpiredWithPastEndDate ? dayjs(leaseTerms.leaseEndDate) : dayjs();

  const resetForm = () => {
    onClose();
    setStep("confirmation");
    reset();
  };

  const handleOk = () => {
    if (step === "confirmation") setStep("finalization");
    else onSubmit();
  };

  const onSubmit = handleSubmit(async (data: FormValues) => {
    const params = {
      leaseId,
      vacatingDate: data.vacatingDate.format("YYYY-MM-DD"),
      notifyUsers: data.notifyUsers.join(", "),
    };
    await earlyTerminateLease(params);
    resetForm();
  });

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      title="Terminate Lease"
      steps={step === "confirmation" ? "Confirm" : "Submit"}
      handleOk={handleOk}
      resetForm={resetForm}
      confirmLoading={isPendingEarlyTerminateLease}
    >
      {step === "confirmation" ? (
        <div>
          <p>This will initiate the lease termination process for the active lease.</p>
          <p>
            Please ensure all dues are settled and both parties have agreed to terminate. This action may affect
            payments, deposits, and access for the tenant.
          </p>
          <p>Are you sure you want to proceed?</p>
        </div>
      ) : (
        <Form name="terminate-lease-form" layout="vertical" onFinish={onSubmit}>
          <Form.Item
            required
            label="Vacating Date"
            validateStatus={errors.vacatingDate ? "error" : ""}
            help={errors.vacatingDate?.message}
          >
            <Controller
              name="vacatingDate"
              control={control}
              rules={{ required: "Please select a date" }}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  style={{ width: "100%" }}
                  onChange={(date) => field.onChange(date)}
                  disabledDate={(current) => current.isBefore(minVacatingDate, "day")}
                />
              )}
            />
          </Form.Item>

          <Form.Item label="Notify Users">
            <Controller
              name="notifyUsers"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  mode="multiple"
                  maxTagCount={3}
                  options={masterUsers.map((item) => ({ label: item.name, value: item.userId }))}
                  onChange={(values) => field.onChange(values)}
                  placeholder="Select users"
                  loading={isLoadingMasterUsers}
                />
              )}
            />
          </Form.Item>
        </Form>
      )}
    </ActionModal>
  );
};
